import { useFormContext } from 'react-hook-form'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { CategoriesSelect } from '@/shared/components/categories-select'
import { LogoUploadField } from '@/shared/components/logo-upload'
import type { CreateBusinessFormValues } from '../../schemas/createBusinessSchema'

interface GeneralInfoStepProps {
  /** Oculta el checkbox "Usar la misma dirección de mi organización".
   *  Se activa cuando el usuario aún no tiene organización (primer negocio). */
  hideOrgAddressOption?: boolean
}

/**
 * Paso 1 del wizard de creación de un negocio: datos generales (logo, nombre,
 * descripción, categorías y dirección compartida). El tipo de facturación y el
 * día de cobro se gestionan a nivel de organización, no aquí.
 */
export function GeneralInfoStep({ hideOrgAddressOption = false }: GeneralInfoStepProps) {
  const { control } = useFormContext<CreateBusinessFormValues>()

  return (
    <div className="grid gap-6 sm:grid-cols-[140px_1fr]">
      <FormField
        control={control}
        name="logo"
        render={({ field }) => (
          <FormItem className="flex items-start justify-center sm:justify-start">
            <LogoUploadField
              value={field.value ?? null}
              onChange={(key) => field.onChange(key ?? '')}
            />
          </FormItem>
        )}
      />

      <div className="grid gap-5">
        <FormField
          control={control}
          name="legalName"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Nombre legal</FormLabel>
              <FormControl>
                <Input required placeholder="Ej. Pepitos Market — Sucursal Vedado" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descripción</FormLabel>
              <FormControl>
                <Textarea placeholder="¿Qué hace este negocio?" {...field} />
              </FormControl>
              <FormDescription>Opcional. Hasta 500 caracteres.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="businessCategoryIds"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>Categorías</FormLabel>
              <FormControl>
                <CategoriesSelect
                  value={field.value ?? []}
                  onChange={(ids) => field.onChange(ids)}
                  invalid={!!fieldState.error}
                />
              </FormControl>
              <FormDescription>
                Selecciona las categorías que mejor describen este negocio.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {!hideOrgAddressOption && (
          <FormField
            control={control}
            name="useSameOrgAddress"
            render={({ field }) => (
              <FormItem>
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-ink">
                  <Checkbox
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                  Usar la misma dirección de mi organización
                </label>
                <FormDescription>
                  Si la activas, se omite el paso de dirección y se usa la dirección principal
                  de tu organización.
                </FormDescription>
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  )
}
